import { default as Popover } from 'antd/es/popover';

import type { Attachment } from 'api';
import { Restricted } from 'containers/Restricted';

import { DocumentIcon } from '@atoms/Icon/DocumentIcon';
import { DownloadIcon } from '@atoms/Icon/DownloadIcon';
import { ImageIcon } from '@atoms/Icon/ImageIcon';
import { TrashIcon } from '@atoms/Icon/TrashIcon';
import { Spinner } from '@atoms/Spinner/Spinner';

type Props = {
  item: Attachment;
  onDelete?: (uuid: string) => void;
  onDownload?: (att: Attachment) => void;
  isDeleting?: boolean;
  isDownloading?: boolean;
};

export const DetailsAttachmentRow = ({
  item,
  onDelete,
  onDownload,
  isDeleting,
  isDownloading
}: Props) => {
  const deletingThis = !!isDeleting;
  const downloadingThis = !!isDownloading;
  const isBusy = isDeleting || isDownloading;

  const isPdf = item.format === 'application/pdf';

  const info = (
    <div className="space-y-1">
      {item.created_at ? (
        <p className="text-xs">{`This file was added ${item.created_at} GMT`}</p>
      ) : null}
    </div>
  );

  const handleActionClick = (key: 'download' | 'delete') => {
    if (isBusy) return;

    if (key === 'delete') onDelete?.(item.uuid);
    if (key === 'download') onDownload?.(item);
  };

  return (
    <div className="flex flex-col gap-2 rounded-full border border-gray-300 p-2">
      <div className="flex items-center justify-between gap-3">
        <Popover content={info}>
          <div className="flex min-w-0 items-center gap-1">
            <div className="relative flex size-7 items-center justify-center rounded">
              {isPdf ? (
                <DocumentIcon className="fill-feedback-info" />
              ) : (
                <ImageIcon className="fill-content-brand-muted" />
              )}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {item.file_name}
              </div>
            </div>
          </div>
        </Popover>

        <Restricted requiredPermission="attachments:manage">
          <div className="flex items-center gap-2">
            {/* Download */}
            <button
              disabled={isBusy}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick('download');
              }}
              className="group rounded-md p-1 disabled:opacity-50"
            >
              {downloadingThis ? (
                <Spinner />
              ) : (
                <DownloadIcon className="size-5 stroke-content-secondary transition-colors group-hover:stroke-action-secondary" />
              )}
            </button>

            {/* Delete */}
            <button
              disabled={isBusy}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick('delete');
              }}
              className="group rounded-md p-1 disabled:opacity-50"
            >
              {deletingThis ? (
                <Spinner />
              ) : (
                <TrashIcon className="size-5 stroke-content-secondary group-hover:stroke-red-500" />
              )}
            </button>
          </div>
        </Restricted>
      </div>
    </div>
  );
};
